import React from 'react';
import { connect } from 'react-redux';
import { Grid, Box, Typography, Chip, Switch } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ButtonInc from '../../../shared-ui/ButtonInc';
import ModalInc from '../../../shared-ui/ModalInc';
import AlertDialog from '../../../components/AlertDialog/index.es6';
import SkeletonWrapper from '../../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import {
  getOrgCustomerStatusConfigsRequest,
  createOrgCustomerStatusConfigRequest,
  updateOrgCustomerStatusConfigRequest,
  deleteOrgCustomerStatusConfigRequest,
  resetCreateOrgCustomerStatusConfigError,
  resetUpdateOrgCustomerStatusConfigError,
  resetDeleteOrgCustomerStatusConfigError,
} from '../../OrganizationCustomerStatus/Redux/actions';
import { formatDateLocale } from '../../../lib/utils.es6';

var DEFAULT_TABS = [
  'profile', 'policies', 'payments', 'quotes',
  'premiums', 'claims', 'sms_activity', 'audits',
];

function makeDefaultTabObjects() {
  return DEFAULT_TABS.map(function(t) {
    return {
      code: t,
      label: t.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }),
      visible: true,
      permission: '',
    };
  });
}

var EMPTY_FORM = {
  code: '',
  label: '',
  tabs: makeDefaultTabObjects(),
  branding: { accent_color: '#6D28D9', density: 'comfortable' },
  active: true,
};

function cloneForm(f) {
  return {
    code: f.code,
    label: f.label,
    tabs: f.tabs.map(function(t) { return Object.assign({}, t); }),
    branding: Object.assign({}, f.branding),
    active: f.active,
  };
}

class CustomerStatusTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAddModal: false,
      showEditModal: false,
      showDeleteConfirm: false,
      selectedItem: null,
      form: cloneForm(EMPTY_FORM),
    };
    this.openAdd = this.openAdd.bind(this);
    this.openEdit = this.openEdit.bind(this);
    this.openDelete = this.openDelete.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.setField = this.setField.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.toggleTabVisible = this.toggleTabVisible.bind(this);
    this.updateTabLabel = this.updateTabLabel.bind(this);
    this.updateTabCode = this.updateTabCode.bind(this);
    this.updateTabPermission = this.updateTabPermission.bind(this);
    this.addCustomTab = this.addCustomTab.bind(this);
    this.removeTab = this.removeTab.bind(this);
    this.setBrandingField = this.setBrandingField.bind(this);
  }

  componentWillMount() {
    this.props.dispatch(
      getOrgCustomerStatusConfigsRequest(this.props.organizationGuid)
    );
  }

  componentDidUpdate(prevProps) {
    var d = this.props.orgCustomerStatusData;
    var p = prevProps.orgCustomerStatusData;
    if (d.created && !p.created) {
      this.setState({ showAddModal: false, form: cloneForm(EMPTY_FORM) });
    }
    if (d.updated && !p.updated) {
      this.setState({ showEditModal: false, selectedItem: null });
    }
    if (d.deleted && !p.deleted) {
      this.setState({ showDeleteConfirm: false, selectedItem: null });
    }
  }

  openAdd() {
    this.setState({ showAddModal: true, form: cloneForm(EMPTY_FORM) });
  }

  openEdit(item) {
    var sd = item.status_page_data || {};
    var tabs = (sd.tabs && sd.tabs.length > 0)
      ? sd.tabs.map(function(t) { return Object.assign({}, t); })
      : makeDefaultTabObjects();
    var branding = sd.branding
      ? Object.assign({}, sd.branding)
      : { accent_color: '#6D28D9', density: 'comfortable' };
    if (!branding.accent_color) { branding.accent_color = '#6D28D9'; }
    if (!branding.density) { branding.density = 'comfortable'; }
    this.setState({
      showEditModal: true,
      selectedItem: item,
      form: {
        code: item.code || '',
        label: sd.label || '',
        tabs: tabs,
        branding: branding,
        active: item.active !== false,
      },
    });
  }

  openDelete(item) {
    this.setState({ showDeleteConfirm: true, selectedItem: item });
  }

  setField(name, value) {
    this.setState(function(s) {
      return { form: Object.assign({}, s.form, { [name]: value }) };
    });
  }

  setBrandingField(name, value) {
    this.setState(function(s) {
      return {
        form: Object.assign({}, s.form, {
          branding: Object.assign({}, s.form.branding, { [name]: value }),
        }),
      };
    });
  }

  onDragEnd(result) {
    if (!result.destination) { return; }
    var src = result.source.index;
    var dst = result.destination.index;
    if (src === dst) { return; }
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t) { return Object.assign({}, t); });
      var removed = tabs.splice(src, 1)[0];
      tabs.splice(dst, 0, removed);
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  toggleTabVisible(index) {
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t, i) {
        return i === index ? Object.assign({}, t, { visible: !t.visible }) : Object.assign({}, t);
      });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  updateTabLabel(index, label) {
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t, i) {
        return i === index ? Object.assign({}, t, { label: label }) : Object.assign({}, t);
      });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  updateTabCode(index, code) {
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t, i) {
        return i === index ? Object.assign({}, t, { code: code }) : Object.assign({}, t);
      });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  updateTabPermission(index, perm) {
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t, i) {
        return i === index ? Object.assign({}, t, { permission: perm }) : Object.assign({}, t);
      });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  addCustomTab() {
    this.setState(function(s) {
      var tabs = s.form.tabs.map(function(t) { return Object.assign({}, t); });
      tabs.push({ code: '', label: '', visible: true, permission: '' });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  removeTab(index) {
    this.setState(function(s) {
      var tabs = s.form.tabs.filter(function(t, i) { return i !== index; });
      return { form: Object.assign({}, s.form, { tabs: tabs }) };
    });
  }

  buildPayload() {
    var f = this.state.form;
    return {
      organization_guid: this.props.organizationGuid,
      code: f.code,
      active: f.active,
      status_page_data: {
        label: f.label,
        tabs: f.tabs,
        branding: f.branding,
      },
    };
  }

  handleCreate() {
    this.props.dispatch(createOrgCustomerStatusConfigRequest(this.buildPayload()));
  }

  handleUpdate() {
    var payload = Object.assign({}, this.buildPayload(), {
      guid: this.state.selectedItem.guid,
    });
    this.props.dispatch(updateOrgCustomerStatusConfigRequest(payload));
  }

  handleDelete() {
    this.props.dispatch(deleteOrgCustomerStatusConfigRequest({
      guid: this.state.selectedItem.guid,
      organization_guid: this.props.organizationGuid,
    }));
  }

  renderLivePreview() {
    var f = this.state.form;
    var accentColor = (f.branding && f.branding.accent_color) ? f.branding.accent_color : '#6D28D9';
    var density = (f.branding && f.branding.density) ? f.branding.density : 'comfortable';
    var visibleTabs = f.tabs.filter(function(t) { return t.visible !== false; });
    var tabPadding = density === 'compact' ? '4px 10px' : density === 'spacious' ? '10px 20px' : '6px 14px';

    return (
      <Box sx={{
        position: 'sticky',
        top: 0,
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        overflow: 'hidden',
        background: '#FAFAFA',
      }}>
        <Box sx={{
          background: '#6D28D9',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
            PREVIEW
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>
            {density}
          </Typography>
        </Box>

        <Box sx={{ background: '#fff', px: 2, py: 1.5, borderBottom: '1px solid #F3F4F6' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
            Customer Status
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
            {f.label || 'Status Page'}
          </Typography>
        </Box>

        <Box sx={{ background: '#fff', px: 1, pt: 0.5, borderBottom: '2px solid #F3F4F6' }}>
          <Box sx={{ display: 'flex', overflowX: 'auto' }}>
            {visibleTabs.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: '#9CA3AF', px: 1, py: 1 }}>
                No visible tabs
              </Typography>
            ) : (
              visibleTabs.map(function(tab, idx) {
                var isFirst = idx === 0;
                var tabLabel = tab.label || tab.code || 'Tab';
                return (
                  <Box key={tab.code || idx} sx={{
                    px: tabPadding.split(' ')[1] || '14px',
                    py: tabPadding.split(' ')[0] || '6px',
                    fontSize: 11,
                    fontWeight: isFirst ? 700 : 500,
                    color: isFirst ? accentColor : '#6B7280',
                    borderBottom: isFirst ? ('2px solid ' + accentColor) : '2px solid transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'color 0.15s',
                  }}>
                    {tabLabel}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, minHeight: 120, background: '#F9FAFB' }}>
          <Box sx={{
            background: '#E5E7EB',
            borderRadius: 1,
            height: 12,
            width: '60%',
            mb: 1,
          }} />
          <Box sx={{
            background: '#E5E7EB',
            borderRadius: 1,
            height: 10,
            width: '80%',
            mb: 1,
          }} />
          <Box sx={{
            background: '#E5E7EB',
            borderRadius: 1,
            height: 10,
            width: '45%',
          }} />
        </Box>

        <Box sx={{ px: 2, py: 1, background: '#F3F4F6', borderTop: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: 10, color: '#9CA3AF' }}>
            {visibleTabs.length} tab{visibleTabs.length !== 1 ? 's' : ''} visible
            {f.code ? (' · ' + f.code) : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  renderBuilderPanel() {
    var self = this;
    var f = this.state.form;
    var accentColor = (f.branding && f.branding.accent_color) ? f.branding.accent_color : '#6D28D9';
    var density = (f.branding && f.branding.density) ? f.branding.density : 'comfortable';

    var inputStyle = {
      display: 'block',
      width: '100%',
      padding: '7px 10px',
      marginTop: 4,
      boxSizing: 'border-box',
      borderRadius: 4,
      border: '1px solid #D1D5DB',
      fontSize: 13,
      outline: 'none',
    };
    var labelStyle = { fontWeight: 600, fontSize: 12, color: '#374151', display: 'block', marginBottom: 2 };
    var smallInputStyle = Object.assign({}, inputStyle, { fontSize: 12, padding: '5px 8px' });

    var densityOptions = ['comfortable', 'compact', 'spacious'];

    return (
      <Box>
        <Grid container spacing={2} style={{ marginTop: 0 }}>
          <Grid item xs={12} sm={6}>
            <label style={labelStyle}>Code *</label>
            <input
              style={inputStyle}
              value={f.code}
              onChange={function(e) { self.setField('code', e.target.value); }}
              placeholder="e.g. standard_status_page"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <label style={labelStyle}>Label</label>
            <input
              style={inputStyle}
              value={f.label}
              onChange={function(e) { self.setField('label', e.target.value); }}
              placeholder="Customer Status Page"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#374151', mb: 1 }}>
            Tabs Configuration
          </Typography>
          <Box sx={{
            border: '1px solid #E5E7EB',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '28px 40px 110px 1fr 110px 32px',
              gap: '4px',
              px: 1,
              py: 0.75,
              background: '#F9FAFB',
              borderBottom: '1px solid #E5E7EB',
            }}>
              <Box />
              <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Show</Typography>
              <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Code</Typography>
              <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Label</Typography>
              <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Permission</Typography>
              <Box />
            </Box>

            <DragDropContext onDragEnd={this.onDragEnd}>
              <Droppable droppableId="tabs-list">
                {function(provided) {
                  return (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {f.tabs.map(function(tab, idx) {
                        var isDefault = DEFAULT_TABS.indexOf(tab.code) !== -1;
                        return (
                          <Draggable key={tab.code || ('custom-' + idx)} draggableId={tab.code || ('custom-' + idx)} index={idx}>
                            {function(dragProvided, dragSnapshot) {
                              return (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '28px 40px 110px 1fr 110px 32px',
                                    gap: '4px',
                                    alignItems: 'center',
                                    px: 1,
                                    py: 0.5,
                                    borderBottom: '1px solid #F3F4F6',
                                    background: dragSnapshot.isDragging ? '#F5F3FF' : (tab.visible !== false ? '#fff' : '#FAFAFA'),
                                    '&:last-child': { borderBottom: 'none' },
                                  }}
                                >
                                  <Box
                                    {...dragProvided.dragHandleProps}
                                    sx={{ color: '#9CA3AF', cursor: 'grab', display: 'flex', alignItems: 'center' }}
                                  >
                                    <DragIndicatorIcon sx={{ fontSize: 16 }} />
                                  </Box>

                                  <Switch
                                    size="small"
                                    checked={tab.visible !== false}
                                    onChange={function() { self.toggleTabVisible(idx); }}
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor },
                                    }}
                                  />

                                  {isDefault ? (
                                    <Chip
                                      label={tab.code}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: 10,
                                        fontFamily: 'monospace',
                                        bgcolor: '#EDE9FE',
                                        color: '#5B21B6',
                                        border: '1px solid #DDD6FE',
                                        maxWidth: 106,
                                      }}
                                    />
                                  ) : (
                                    <input
                                      style={Object.assign({}, smallInputStyle, { fontFamily: 'monospace' })}
                                      value={tab.code}
                                      onChange={function(e) { self.updateTabCode(idx, e.target.value); }}
                                      placeholder="code"
                                    />
                                  )}

                                  <input
                                    style={Object.assign({}, smallInputStyle, {
                                      opacity: tab.visible !== false ? 1 : 0.5,
                                    })}
                                    value={tab.label}
                                    onChange={function(e) { self.updateTabLabel(idx, e.target.value); }}
                                    placeholder={tab.code || 'Label'}
                                  />

                                  <input
                                    style={Object.assign({}, smallInputStyle, { fontSize: 11 })}
                                    value={tab.permission || ''}
                                    onChange={function(e) { self.updateTabPermission(idx, e.target.value); }}
                                    placeholder="e.g. VIEW_PROFILE"
                                  />

                                  <Box
                                    component="button"
                                    onClick={function() { self.removeTab(idx); }}
                                    disabled={isDefault}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 26,
                                      height: 26,
                                      borderRadius: 1,
                                      border: 'none',
                                      cursor: isDefault ? 'not-allowed' : 'pointer',
                                      background: isDefault ? 'transparent' : '#FEF2F2',
                                      color: isDefault ? '#D1D5DB' : '#B91C1C',
                                      '&:hover': { background: isDefault ? 'transparent' : '#FEE2E2' },
                                      padding: 0,
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                  </Box>
                                </Box>
                              );
                            }}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  );
                }}
              </Droppable>
            </DragDropContext>
          </Box>

          <Box
            component="button"
            onClick={this.addCustomTab}
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 12,
              color: '#6D28D9',
              background: 'none',
              border: '1px dashed #DDD6FE',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              cursor: 'pointer',
              '&:hover': { background: '#F5F3FF' },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add Custom Tab
          </Box>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#374151', mb: 1.5 }}>
            Branding
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <label style={labelStyle}>Accent Color</label>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <input
                  type="color"
                  value={accentColor}
                  onChange={function(e) { self.setBrandingField('accent_color', e.target.value); }}
                  style={{
                    width: 36,
                    height: 32,
                    border: '1px solid #D1D5DB',
                    borderRadius: 4,
                    cursor: 'pointer',
                    padding: 2,
                    background: 'none',
                  }}
                />
                <input
                  style={Object.assign({}, inputStyle, { flex: 1, marginTop: 0, fontFamily: 'monospace', fontSize: 13 })}
                  value={accentColor}
                  onChange={function(e) { self.setBrandingField('accent_color', e.target.value); }}
                  placeholder="#6D28D9"
                  maxLength={7}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <label style={labelStyle}>Density</label>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                {densityOptions.map(function(opt) {
                  var isSelected = density === opt;
                  return (
                    <Box
                      key={opt}
                      component="button"
                      onClick={function() { self.setBrandingField('density', opt); }}
                      sx={{
                        px: 1.5,
                        py: 0.6,
                        fontSize: 11,
                        fontWeight: isSelected ? 700 : 500,
                        border: isSelected ? ('1.5px solid ' + accentColor) : '1px solid #D1D5DB',
                        borderRadius: 1.5,
                        background: isSelected ? (accentColor + '15') : '#fff',
                        color: isSelected ? accentColor : '#6B7280',
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {opt}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              checked={f.active}
              onChange={function(e) { self.setField('active', e.target.checked); }}
              style={{ marginRight: 4 }}
            />
            Active
          </label>
        </Box>
      </Box>
    );
  }

  renderFormFields() {
    return (
      <Grid container spacing={3} style={{ marginTop: 0 }}>
        <Grid item xs={12} md={7}>
          {this.renderBuilderPanel()}
        </Grid>
        <Grid item xs={12} md={5}>
          {this.renderLivePreview()}
        </Grid>
      </Grid>
    );
  }

  renderCard(item) {
    var sd = item.status_page_data || {};
    var tabs = sd.tabs || [];
    var visibleTabs = tabs.filter(function(t) { return t.visible !== false; });
    return (
      <Grid item xs={12} sm={6} md={4} key={item.guid}>
        <Box sx={{
          border: '1px solid #E5E7EB', borderRadius: 2,
          overflow: 'hidden', height: '100%',
          display: 'flex', flexDirection: 'column',
        }}>
          <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, borderBottom: '1px solid #F3F4F6',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                {sd.label || item.code}
              </Typography>
              <Typography variant="body2"
                sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', mt: 0.25 }}>
                {item.code}
              </Typography>
            </Box>
            <Chip label={item.active !== false ? 'Active' : 'Inactive'} size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 600,
                bgcolor: item.active !== false ? '#F0FDF4' : '#FEF2F2',
                color: item.active !== false ? '#15803D' : '#B91C1C',
                border: item.active !== false ? '1px solid #BBF7D0' : '1px solid #FECACA' }} />
          </Box>
          <Box sx={{ px: 2.5, py: 1.5, flex: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: '#6B7280', mb: 1 }}>
              {visibleTabs.length} visible tab{visibleTabs.length !== 1 ? 's' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {visibleTabs.slice(0, 5).map(function(t) {
                return (
                  <Chip key={t.code} label={t.label || t.code} size="small"
                    sx={{ height: 18, fontSize: 10, bgcolor: '#F5F3FF',
                      color: '#6D28D9', border: '1px solid #DDD6FE' }} />
                );
              })}
              {visibleTabs.length > 5 && (
                <Chip label={'+ ' + (visibleTabs.length - 5)} size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: '#F9FAFB', color: '#9CA3AF' }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ fontSize: 11, color: '#9CA3AF', mt: 1 }}>
              Created: {item.created_at ? formatDateLocale(item.created_at) : '—'}
            </Typography>
          </Box>
          <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #F3F4F6',
            display: 'flex', gap: 1 }}>
            <ButtonInc btnType="secondary"
              onClick={function() { this.openEdit(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12 }}>Edit</ButtonInc>
            <ButtonInc btnType="secondary"
              onClick={function() { this.openDelete(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12, color: '#B91C1C', borderColor: '#FECACA' }}>
              Delete
            </ButtonInc>
          </Box>
        </Box>
      </Grid>
    );
  }

  render() {
    var d = this.props.orgCustomerStatusData;
    var s = this.state;
    var list = d.list || [];

    if (d.loading) { return <SkeletonWrapper loading={true} />; }

    return (
      <div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6"
              sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
              Customer Status Page Configs
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, color: '#6B7280', mt: 0.25 }}>
              Define which tabs and fields appear on the customer status page.
            </Typography>
          </Box>
          <ButtonInc variant="contained" onClick={this.openAdd}>
            + Add Config
          </ButtonInc>
        </Box>

        {list.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#9CA3AF' }}>
            <Typography variant="body1" sx={{ fontSize: 15 }}>
              No customer status configs yet.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, mt: 0.5 }}>
              Add one to configure the status page for this organization&apos;s partners.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {list.map(this.renderCard.bind(this))}
          </Grid>
        )}

        <ModalInc modalTitle="Add Customer Status Config" open={s.showAddModal}
          close={function() { this.setState({ showAddModal: false }); }.bind(this)}
          fullWidth maxWidth="xl"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showAddModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.creating}
              onClick={this.handleCreate}>Save</ButtonInc>,
          ]}>
          {this.renderFormFields()}
        </ModalInc>

        <ModalInc modalTitle="Edit Customer Status Config" open={s.showEditModal}
          close={function() { this.setState({ showEditModal: false }); }.bind(this)}
          fullWidth maxWidth="xl"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showEditModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.updating}
              onClick={this.handleUpdate}>Save</ButtonInc>,
          ]}>
          {this.renderFormFields()}
        </ModalInc>

        {s.showDeleteConfirm && (
          <AlertDialog show={s.showDeleteConfirm} title="Delete Config"
            type="warning" showConfirm confirmBtnText="Delete" showCancel
            onConfirm={this.handleDelete}
            onCancel={function() {
              this.setState({ showDeleteConfirm: false, selectedItem: null });
            }.bind(this)}>
            Delete <strong>{s.selectedItem && s.selectedItem.code}</strong>?
          </AlertDialog>
        )}

        {d.createError && (
          <AlertDialog show={!!d.createError} title="Error" type="danger"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetCreateOrgCustomerStatusConfigError());
            }.bind(this)}>
            {d.createError.message || JSON.stringify(d.createError)}
          </AlertDialog>
        )}
        {d.updateError && (
          <AlertDialog show={!!d.updateError} title="Error" type="danger"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetUpdateOrgCustomerStatusConfigError());
            }.bind(this)}>
            {d.updateError.message || JSON.stringify(d.updateError)}
          </AlertDialog>
        )}
        {d.deleteError && (
          <AlertDialog show={!!d.deleteError} title="Error" type="danger"
            showConfirm confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetDeleteOrgCustomerStatusConfigError());
            }.bind(this)}>
            {d.deleteError.message || JSON.stringify(d.deleteError)}
          </AlertDialog>
        )}
      </div>
    );
  }
}

export default connect(function(state) {
  return { orgCustomerStatusData: state.orgCustomerStatusConfigsData };
})(CustomerStatusTab);
